import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import { useAuth } from "../../../contexts/AuthContext";
import axiosInstance from "../../../axiosInstance";
import "./Products.scss";
import { toast } from "react-toastify";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import AccessDenied from "../../AccessDenied";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import CustomCombinationModal from "./CustomCombinationModal";

// Organized units by category for better UX
const UNIT_CATEGORIES = {
  "Length / Distance": [
    "MM",
    "CM",
    "METER",
    "KM",
    "INCH",
    "FOOT",
    "YARD",
    "MILE",
  ],
  Area: [
    "SQ_MM",
    "SQ_CM",
    "SQ_METER",
    "SQ_KM",
    "SQ_INCH",
    "SQ_FOOT",
    "SQ_YARD",
    "SQ_MILE",
    "ACRE",
    "HECTARE",
  ],
  "Weight / Mass": ["MG", "GRAM", "KG", "TON", "OUNCE", "POUND", "QUINTAL"],
  "Volume / Capacity": [
    "ML",
    "LITER",
    "CUBIC_CM",
    "CUBIC_METER",
    "CUBIC_INCH",
    "CUBIC_FOOT",
    "GALLON",
    "BARREL",
  ],
  "Quantity / Count": [
    "PIECE",
    "UNIT",
    "PACK",
    "BOX",
    "DOZEN",
    "BUNDLE",
    "SET",
    "PAIR",
    "CARTON",
    "CASE",
    "PALLET",
  ],
  Time: ["SECOND", "MINUTE", "HOUR", "DAY", "WEEK", "MONTH", "YEAR"],
  "Digital / Data": ["BYTE", "KB", "MB", "GB", "TB"],
  "Energy / Power": [
    "WATT",
    "KILOWATT",
    "MEGAWATT",
    "JOULE",
    "CALORIE",
    "KCAL",
    "VOLT",
    "AMPERE",
  ],
  Finance: ["PERCENT", "CURRENCY", "POINT"],
  Miscellaneous: [
    "SHEET",
    "ROLL",
    "STRIP",
    "LENGTH",
    "WEIGHT",
    "VOLUME",
    "AREA",
    "SIZE",
  ],
  Other: ["OTHER"],
};

// Component for unit dropdown with search
const UnitDropdown = ({ name, value, onChange, className = "form-input" }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Flatten all units with their categories for searching
  const allUnits = Object.entries(UNIT_CATEGORIES).flatMap(
    ([category, units]) => units.map((unit) => ({ unit, category })),
  );

  // Filter units based on search
  const filteredUnits = searchTerm
    ? allUnits.filter(
        ({ unit, category }) =>
          unit.toLowerCase().includes(searchTerm.toLowerCase()) ||
          category.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : allUnits;

  // Group filtered units by category
  const groupedFilteredUnits = filteredUnits.reduce(
    (acc, { unit, category }) => {
      if (!acc[category]) acc[category] = [];
      acc[category].push(unit);
      return acc;
    },
    {},
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectUnit = (unit) => {
    onChange({ target: { name, value: unit } });
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div
      className="unit-dropdown-wrapper"
      ref={dropdownRef}
      style={{ position: "relative" }}
    >
      <div
        className={className}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "white",
        }}
      >
        <span>{value ? value.replace(/_/g, " ") : "Select Unit"}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: "4px",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "8px",
            maxHeight: "300px",
            overflow: "hidden",
            zIndex: 1000,
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
            <input
              type="text"
              placeholder="Search units..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>

          <div style={{ maxHeight: "240px", overflowY: "auto" }}>
            {Object.keys(groupedFilteredUnits).length > 0 ? (
              Object.entries(groupedFilteredUnits).map(([category, units]) => (
                <div key={category}>
                  <div
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#f5f5f5",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#666",
                      position: "sticky",
                      top: 0,
                    }}
                  >
                    {category}
                  </div>
                  {units.map((unit) => (
                    <div
                      key={unit}
                      onClick={() => handleSelectUnit(unit)}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        backgroundColor: value === unit ? "#e3f2fd" : "white",
                        fontSize: "14px",
                      }}
                      onMouseEnter={(e) => {
                        if (value !== unit)
                          e.target.style.backgroundColor = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        if (value !== unit)
                          e.target.style.backgroundColor = "white";
                      }}
                    >
                      {unit.replace(/_/g, " ")}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div
                style={{ padding: "12px", textAlign: "center", color: "#999" }}
              >
                No units found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const normalizeCombinations = (combinations = []) =>
  combinations.filter((c) => Number.isInteger(c.combinationId));

const Products = ({ markPageAsCompleted }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupAction, setPopupAction] = useState(() => () => {});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [products, setProducts] = useState([]);
  const [editableProducts, setEditableProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [rawMaterialSearch, setRawMaterialSearch] = useState("");
  const [pricingEnabled, setPricingEnabled] = useState(false);
  const [openedFromTopButton, setOpenedFromTopButton] = useState(false);
  const [selectedProductsQueue, setSelectedProductsQueue] = useState([]);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // ---- Sidebar collapse (whole aside) — persisted so it stays how the
  // user left it across reloads -------------------------------------
  const SIDEBAR_COLLAPSED_KEY = "products_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable (e.g. private mode) — fail silently
      }
      return next;
    });
  };

  // ---- "How It Works" card collapse — independent of the sidebar
  // collapse above, also persisted -------------------------------------
  const INFO_CARD_COLLAPSED_KEY = "products_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable — fail silently
      }
      return next;
    });
  };

  // Pricing related states
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedProductForPricing, setSelectedProductForPricing] =
    useState(null);
  const [pricingCombinations, setPricingCombinations] = useState([]);
  const [editablePricingCombinations, setEditablePricingCombinations] =
    useState([]);
  const [pricingUpdateEnabled, setPricingUpdateEnabled] = useState(false);

  // Category hierarchy states
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [mainCategoryDetails, setMainCategoryDetails] = useState(null);
  const [subCategoryDetails, setSubCategoryDetails] = useState(null);
  const [loadingCategoryDetails, setLoadingCategoryDetails] = useState(false);
  const [productsPricingData, setProductsPricingData] = useState({});
  const [initialValues, setInitialValues] = useState({
    id: 0,
    productCode: "",
    productType: "TRADING",
    name: "",
    description: "",
    status: "ENABLED",
    tags: "",
    unit: "",
    categoryId: null,
    brandId: null,
    clientId: null,
    operationCategoryId: null,
    rawMaterialIds: [],
    specifications: [
      {
        id: 0,
        specName: "",
        values: [
          {
            id: 0,
            value: "",
          },
        ],
      },
    ],
  });

  const [showCustomComboModal, setShowCustomComboModal] = useState(false);
  const [selectedProductForCombo, setSelectedProductForCombo] = useState(null);
  const [autoGenerateCombination, setAutoGenerateCombination] = useState(false);
  const [loadingAutoGenerate, setLoadingAutoGenerate] = useState(false);

  // Derived, real-data metric stats (no fabricated numbers)
  const productStats = useMemo(() => {
    const total = products.length;
    const active = products.filter((p) => p.status === "ENABLED").length;
    const disabled = products.filter((p) => p.status === "DISABLED").length;
    const priced = products.filter((p) => {
      const pricing = productsPricingData[p.id];
      return pricing && pricing.length > 0;
    }).length;
    return { total, active, disabled, priced };
  }, [products, productsPricingData]);

  // Get the appropriate base URL based on user authorities
  const getApiBaseUrl = useCallback(() => {
    const authorities = user?.authorities || [];

    if (authorities.includes("ROLE_ERP-ADMIN")) {
      return process.env.REACT_APP_API_ERP_ADMIN_URL;
    } else if (authorities.includes("ROLE_INVENTORY-MANAGER")) {
      return process.env.REACT_APP_API_INV_MANAGER_URL;
    } else if (authorities.includes("ROLE_CRM-ADMIN")) {
      return process.env.REACT_APP_API_CRM_ADMIN_URL;
    }

    return process.env.REACT_APP_API_ERP_ADMIN_URL;
  }, [user]);

  const fetchAllProductsPricing = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();

      const response = await axiosInstance.get(
        `${baseUrl}/products/pricing/combinations/all`,
      );

      const pricingMap = {};
      (response.data?.data || []).forEach((item) => {
        pricingMap[item.productId] = normalizeCombinations(item.combinations);
      });

      setProductsPricingData(pricingMap);
    } catch (error) {
      console.error("Error fetching products pricing:", error);
      setProductsPricingData({});
    }
  }, [getApiBaseUrl]);

  const fetchCategories = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axiosInstance.get(`${baseUrl}/categories`);
      setCategories(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [getApiBaseUrl]);

  const fetchCategoryDetails = useCallback(
    async (categoryId) => {
      if (!categoryId) return null;

      setLoadingCategoryDetails(true);
      try {
        const baseUrl = getApiBaseUrl();
        const response = await axiosInstance.get(
          `${baseUrl}/categories/${categoryId}`,
        );
        return response.data?.data || null;
      } catch (error) {
        console.error("Error fetching category details:", error);
        return null;
      } finally {
        setLoadingCategoryDetails(false);
      }
    },
    [getApiBaseUrl],
  );

  const handleMainCategorySelect = async (categoryId, formikSetFieldValue) => {
    // Toggle off if clicking the already-selected main category
    if (selectedMainCategory === categoryId) {
      setSelectedMainCategory(null);
      setSelectedSubCategory(null);
      setMainCategoryDetails(null);
      setSubCategoryDetails(null);
      formikSetFieldValue("categoryId", null);
      return;
    }

    setSelectedMainCategory(categoryId);
    setSelectedSubCategory(null);
    setSubCategoryDetails(null);

    formikSetFieldValue("categoryId", categoryId);

    const details = await fetchCategoryDetails(categoryId);
    setMainCategoryDetails(details);
  };

  const handleSubCategorySelect = async (categoryId, formikSetFieldValue) => {
    // Toggle off if clicking the already-selected sub category
    if (selectedSubCategory === categoryId) {
      setSelectedSubCategory(null);
      setSubCategoryDetails(null);
      formikSetFieldValue("categoryId", selectedMainCategory || null);
      return;
    }

    setSelectedSubCategory(categoryId);

    formikSetFieldValue("categoryId", categoryId);

    const details = await fetchCategoryDetails(categoryId);
    setSubCategoryDetails(details);
  };

  const fetchRawMaterials = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axiosInstance.get(`${baseUrl}/raw-materials`);
      setRawMaterials(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching raw materials:", error);
    }
  }, [getApiBaseUrl]);

  const fetchBrands = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axiosInstance.get(`${baseUrl}/brands`);
      setBrands(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  }, [getApiBaseUrl]);

  const fetchProducts = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axiosInstance.get(`${baseUrl}/products`);
      const productsData = (response.data?.data || []).map((product) => ({
        ...product,
        status: product.status?.toUpperCase() || "ENABLED",
        category: product.category || { id: null, name: "-" },
        brand: product.brand || { id: null, brandName: "-" },
        client: product.client || { id: null, name: "-" },
        operationCategory: product.operationCategory || { id: null, name: "-" },
        rawMaterialIds: product.rawMaterials?.map((rm) => rm.id) || [],
        specifications: product.specifications || [],
        productStock: product.productStock || {
          currentStock: 0,
          unit: "PIECE",
        },
      }));

      setProducts(productsData);
      setEditableProducts(productsData.map((p) => ({ ...p })));

      if (response.data?.data?.length > 0) {
        markPageAsCompleted("Products");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, [getApiBaseUrl, markPageAsCompleted]);

  const fetchProductPricingCombinations = useCallback(async (productId) => {
    try {
      const invManagerUrl = process.env.REACT_APP_API_INV_MANAGER_URL;
      const response = await axiosInstance.get(
        `${invManagerUrl}/products/pricing/${productId}/combinations`,
      );
      return response.data?.data || [];
    } catch (error) {
      console.error("Error fetching product pricing:", error);
      return [];
    }
  }, []);

  const fetchAutoGenerateStatus = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_CRM_ADMIN_URL;

      const response = await axiosInstance.get(
        `${baseUrl}/company-settings/auto-generate/product/combination/status`,
      );

      const value = response.data?.data;

      // backend returns string sometimes → normalize
      setAutoGenerateCombination(value === true || value === "true");
    } catch (error) {
      console.error("Error fetching auto combination status:", error);
    }
  }, []);

  useEffect(() => {
    const allowedRoles = [
      "ROLE_ERP-ADMIN",
      "ROLE_INVENTORY-MANAGER",
      "ROLE_CRM-ADMIN",
    ];

    if (allowedRoles.some((role) => user?.authorities?.includes(role))) {
      fetchCategories();
      fetchBrands();
      fetchProducts();
      fetchRawMaterials();
      fetchAllProductsPricing();
      fetchAutoGenerateStatus();
    }
  }, [
    fetchCategories,
    fetchBrands,
    fetchProducts,
    fetchRawMaterials,
    fetchAllProductsPricing,
    fetchAutoGenerateStatus,
    user,
  ]);

  const handleSelectAll = () => {
    const allProductIds = products.map((product) => product.id);
    if (selectAll) {
      setSelectedProducts([]);
      setSelectAll(false);
    } else {
      setSelectedProducts(allProductIds);
      setSelectAll(true);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  useEffect(() => {
    const allProductIds = products.map((product) => product.id);
    const allSelected =
      allProductIds.length > 0 &&
      allProductIds.every((id) => selectedProducts.includes(id));
    setSelectAll(allSelected);
  }, [selectedProducts, products]);

  useEffect(() => {
    setPricingEnabled(selectedProducts.length >= 2);
  }, [selectedProducts]);

  const handleBatchDelete = () => {
    if (selectedProducts.length === 0) {
      toast.warning("Please select at least one product to delete");
      return;
    }

    setPopupAction(() => async () => {
      try {
        const baseUrl = getApiBaseUrl();
        await axiosInstance.delete(`${baseUrl}/products/bulk`, {
          data: selectedProducts,
        });

        toast.success(
          `Successfully deleted ${selectedProducts.length} product(s)`,
        );
        setSelectedProducts([]);
        setSelectAll(false);
        fetchProducts();
        markPageAsCompleted("Products");
      } catch (error) {
        console.error("Error deleting products:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });

    setShowWarningPopup(true);
  };

  const handleBulkUpdateClick = () => {
    if (bulkUpdateEnabled) {
      const hasEmptyNames = editableProducts.some(
        (product) => !product.name || product.name.trim() === "",
      );
      if (hasEmptyNames) {
        toast.error("All product names must be filled out before submitting.");
        return;
      }

      const payload = editableProducts.map((product) => ({
        id: product.id,
        productCode: product.productCode || "",
        productType: product.productType.toUpperCase(),
        name: product.name,
        description: product.description,
        status: product.status,
        tags: product.tags,
        unit: product.unit || "MM",
        categoryId: product.category?.id || product.categoryId,
        brandId: product.brand?.id || product.brandId,
        clientId: product.client?.id || product.clientId,
        operationCategoryId:
          product.operationCategory?.id || product.operationCategoryId,
        rawMaterialIds: product.rawMaterials?.map((rm) => rm.id) || [],
        specifications: product.specifications || [],
      }));

      const baseUrl = getApiBaseUrl();

      axiosInstance
        .put(`${baseUrl}/products/bulk`, payload)
        .then(() => {
          toast.success("Bulk update submitted successfully");
          setProducts([...editableProducts]);
          markPageAsCompleted("Products");
        })
        .catch((error) => {
          console.error("Error submitting bulk update:", error);
        });
    }

    setBulkUpdateEnabled((prev) => !prev);
  };

  const handleInlineEdit = (productId, field, value) => {
    setEditableProducts((prev) =>
      prev.map((product) =>
        product.id === productId ? { ...product, [field]: value } : product,
      ),
    );
  };

  const handleToggleAutoGenerate = async () => {
    const newValue = !autoGenerateCombination;

    try {
      setLoadingAutoGenerate(true);

      const baseUrl = process.env.REACT_APP_API_CRM_ADMIN_URL;

      await axiosInstance.put(
        `${baseUrl}/company-settings/auto-generate/product/combination/status`,
        newValue,
        { headers: { "Content-Type": "application/json" } },
      );

      // 🔥 IMPORTANT — re-fetch actual status from server
      await fetchAutoGenerateStatus();

      toast.success(
        `Auto combination generation ${newValue ? "enabled" : "disabled"}`,
      );
    } catch (error) {
      console.error("Error updating auto combination setting:", error);
      toast.error("Failed to update setting");
    } finally {
      setLoadingAutoGenerate(false);
    }
  };

  const handleEditProduct = async (productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setIsEditing(true);
      setInitialValues({
        ...product,
        categoryId: product.category?.id || null,
        brandId: product.brand?.id || null,
        clientId: product.client?.id || null,
        operationCategoryId: product.operationCategory?.id || null,
        productType: product.productType?.toUpperCase(),
        unit: product.unit || "",
        rawMaterialIds: product.rawMaterialIds || [],
        specifications:
          product.specifications.length > 0
            ? product.specifications
            : [
                {
                  id: 0,
                  specName: "",
                  values: [{ id: 0, value: "" }],
                },
              ],
      });

      // Load category hierarchy if product has a category
      if (product.category?.id) {
        const categoryDetails = await fetchCategoryDetails(product.category.id);
        if (categoryDetails) {
          setSelectedMainCategory(product.category.id);
          setMainCategoryDetails(categoryDetails);
        }
      }

      setShowForm(true);
    }
  };

  const handleDelete = async (productId) => {
    try {
      const baseUrl = getApiBaseUrl();
      await axiosInstance.delete(`${baseUrl}/products/${productId}`);
      toast.success("Product deleted successfully.");
      fetchProducts();
      markPageAsCompleted("Products");
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const toggleProductStatus = async (productId, currentStatus) => {
    const isCurrentlyEnabled = currentStatus === "ENABLED";
    const newStatus = isCurrentlyEnabled ? "DISABLED" : "ENABLED";
    const action = isCurrentlyEnabled ? "disable" : "enable";
    const endpoint = `${getApiBaseUrl()}/products/${productId}/${action}`;

    try {
      await axiosInstance.put(endpoint);

      const updateState = (prevState) =>
        prevState.map((product) =>
          product.id === productId
            ? { ...product, status: newStatus }
            : product,
        );

      setProducts(updateState);
      setEditableProducts(updateState);

      toast.success(`Product status changed to ${newStatus.toLowerCase()}`);
      markPageAsCompleted("Products");
    } catch (error) {
      console.error(`Error changing status:`, error);
    }
  };

  // Handle opening pricing modal
  const handleUpdatePricing = async (productId) => {
    setOpenedFromTopButton(false);

    const product = products.find((p) => p.id === productId);
    if (!product) return;

    setSelectedProductForPricing(product);

    const combinations = await fetchProductPricingCombinations(productId);

    setPricingCombinations(combinations);
    setEditablePricingCombinations(combinations.map((c) => ({ ...c })));

    setPricingUpdateEnabled(true);
    setShowPricingModal(true);
  };

  // Handle inline pricing edit
  const handlePricingInlineEdit = (combinationId, field, value) => {
    setEditablePricingCombinations((prev) =>
      prev.map((combo) =>
        combo.combinationId === combinationId
          ? {
              ...combo,
              [field]:
                field === "available"
                  ? value
                  : field === "hsnCode" ||
                      field === "skuCode" ||
                      field === "combinationName"
                    ? value
                    : field === "costPrice" ||
                        field === "marginPercentage" ||
                        field === "discountPrice" ||
                        field === "taxRate" ||
                        field === "minimumStockQuantity"
                      ? value === ""
                        ? ""
                        : parseFloat(value)
                      : value,
            }
          : combo,
      ),
    );
  };

  // Handle deleting a combination from pricing modal
  const handleDeleteCombination = async (combinationId) => {
    // Guard: invalid or unsaved combination
    if (!Number.isInteger(combinationId) || combinationId <= 0) {
      toast.error("Invalid combination. Please refresh pricing data.");
      return;
    }

    try {
      const baseUrl = getApiBaseUrl();

      await axiosInstance.delete(
        `${baseUrl}/product-combinations/${combinationId}`,
      );

      // Remove from editable state
      setEditablePricingCombinations((prev) =>
        prev.filter((c) => c.combinationId !== combinationId),
      );

      // Remove from read-only state (safety)
      setPricingCombinations((prev) =>
        prev.filter((c) => c.combinationId !== combinationId),
      );

      toast.success("Combination deleted successfully");
    } catch (error) {
      console.error("Error deleting combination:", error);
    }
  };

  const handleTopEnablePricing = async () => {
    if (selectedProducts.length === 0) {
      toast.warning("Please select at least one product");
      return;
    }

    // Show info toast about the process
    toast.info(
      `You will update pricing for ${selectedProducts.length} product(s) one by one. Each product will open in a separate modal.`,
      { autoClose: 5000 },
    );

    setOpenedFromTopButton(true);
    setSelectedProductsQueue([...selectedProducts]);
    setCurrentProductIndex(0);

    // Load first product
    const firstProductId = selectedProducts[0];
    const product = products.find((p) => p.id === firstProductId);
    if (!product) return;

    setSelectedProductForPricing(product);

    const combinations = await fetchProductPricingCombinations(firstProductId);
    setPricingCombinations(combinations);
    setEditablePricingCombinations(combinations.map((c) => ({ ...c })));

    setPricingUpdateEnabled(true);
    setShowPricingModal(true);
  };

  // Copy pricing to all combinations
  const handleCopyPricingToAll = () => {
    if (editablePricingCombinations.length === 0) return;

    const firstCombo = editablePricingCombinations[0];
    const {
      costPrice,
      marginPercentage,
      discountPrice,
      taxRate,
      hsnCode,
      minimumStockQuantity,
    } = firstCombo;

    setEditablePricingCombinations((prev) =>
      prev.map((combo) => ({
        ...combo,
        costPrice,
        marginPercentage,
        discountPrice,
        taxRate,
        hsnCode: hsnCode || "",
        minimumStockQuantity: minimumStockQuantity || 0,
      })),
    );

    toast.success("Pricing and HSN Code copied to all combinations");
  };

  const getBulkUploadBaseUrl = () => {
    const authorities = user?.authorities || [];

    if (authorities.includes("ROLE_ERP-ADMIN"))
      return process.env.REACT_APP_API_ERP_ADMIN_URL;

    if (authorities.includes("ROLE_INVENTORY-MANAGER"))
      return process.env.REACT_APP_API_INV_MANAGER_URL;

    if (authorities.includes("ROLE_CRM-ADMIN"))
      return process.env.REACT_APP_API_CRM_ADMIN_URL;

    return process.env.REACT_APP_API_INV_MANAGER_URL;
  };

  const downloadTemplate = async () => {
    try {
      const baseUrl = getBulkUploadBaseUrl();

      const response = await axiosInstance.get(
        `${baseUrl}/products/template/crm/download-template`,
        { responseType: "blob" },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "product_template.xlsx");
      link.click();

      toast.success("Template downloaded");
    } catch (e) {
      console.error("Error downloading template:", e);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    try {
      const baseUrl = getBulkUploadBaseUrl();

      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axiosInstance.post(
        `${baseUrl}/products/template/crm/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (response.data.status === 200) {
        toast.success("Uploaded successfully");
        setShowUploadModal(false);
        setSelectedFile(null);
        fetchProducts();
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  // Submit pricing updates
  const handleSubmitPricing = async () => {
    if (!selectedProductForPricing) return;

    // Validate that all SKU codes are filled
    const missingSkus = editablePricingCombinations.filter(
      (combo) => !combo.skuCode || combo.skuCode.trim() === "",
    );

    if (missingSkus.length > 0) {
      toast.error("All SKU codes must be filled before submitting.");
      return;
    }

    try {
      const invManagerUrl = process.env.REACT_APP_API_INV_MANAGER_URL;

      const payload = editablePricingCombinations.map((combo) => ({
        combinationId: combo.combinationId,
        specificationValueIds: combo.specificationValues.map(
          (sv) => sv.valueId,
        ),
        combinationName: combo.combinationName?.trim() || null, // ✅ optional
        hsnCode: combo.hsnCode || "",
        skuCode: combo.skuCode?.trim() || "",
        costPrice: parseFloat(combo.costPrice) || 0,
        marginPercentage: parseFloat(combo.marginPercentage) || 0,
        discountPrice: parseFloat(combo.discountPrice) || 0,
        taxRate: parseFloat(combo.taxRate) || 0,
        minimumStockQuantity: parseInt(combo.minimumStockQuantity) || 0,
        available: combo.available !== false,
      }));

      await axiosInstance.put(
        `${invManagerUrl}/products/pricing/${selectedProductForPricing.id}/combinations`,
        payload,
      );

      toast.success(`Pricing updated for ${selectedProductForPricing.name} `);
      setPricingCombinations([...editablePricingCombinations]);

      // Check if there are more products in the queue
      if (
        openedFromTopButton &&
        currentProductIndex < selectedProductsQueue.length - 1
      ) {
        // Load next product
        const nextIndex = currentProductIndex + 1;
        setCurrentProductIndex(nextIndex);

        const nextProductId = selectedProductsQueue[nextIndex];
        const nextProduct = products.find((p) => p.id === nextProductId);

        if (nextProduct) {
          setSelectedProductForPricing(nextProduct);

          const combinations =
            await fetchProductPricingCombinations(nextProductId);
          setPricingCombinations(combinations);
          setEditablePricingCombinations(combinations.map((c) => ({ ...c })));

          setPricingUpdateEnabled(true);
          // Modal stays open
        }
      } else {
        // All products done
        setPricingUpdateEnabled(false);
        setShowPricingModal(false);
        setSelectedProductForPricing(null);
        setSelectedProductsQueue([]);
        setCurrentProductIndex(0);

        if (openedFromTopButton) {
          toast.success("All product pricing updated successfully!");
        }
      }
    } catch (error) {
      console.error("Error updating pricing:", error);
    }
  };

  const handleAddCustomCombination = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) {
      toast.warning("Product not found");
      return;
    }

    const combinations = productsPricingData[productId] || [];

    setSelectedProductForCombo({
      ...product,
      combinations, // ✅ attach combinations here
    });

    setShowCustomComboModal(true);
  };

  const handleCustomComboSuccess = () => {
    fetchAllProductsPricing();
    toast.success("Please refresh pricing data to see the new combination");
  };

  const productValidationSchema = Yup.object().shape({
    name: Yup.string().required("Product Name is required"),
    productType: Yup.string().required("Product Type is required"),
    unit: Yup.string().when([], {
      is: () => !isEditing,
      then: (schema) => schema.required("Unit is required for new products"),
      otherwise: (schema) => schema.notRequired(),
    }),
    categoryId: Yup.number().nullable(),
    brandId: Yup.number().nullable(),
    clientId: Yup.number().nullable(),
    operationCategoryId: Yup.number().nullable(),
    rawMaterialIds: Yup.array().nullable(), // Changed from required validation
    specifications: Yup.array().of(
      Yup.object().shape({
        specName: Yup.string().when("values", {
          is: (values) =>
            values && values.some((v) => v.value && v.value.trim()),
          then: (schema) =>
            schema.trim().required("Specification name is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        values: Yup.array().of(
          Yup.object().shape({
            value: Yup.string().notRequired(),
          }),
        ),
      }),
    ),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const baseUrl = getApiBaseUrl();

      // Filter and clean specifications
      const cleanedSpecifications = values.specifications
        .filter((spec) => spec.specName && spec.specName.trim())
        .map((spec) => ({
          id: isEditing ? spec.id || 0 : 0,
          specName: spec.specName.trim(), // Trim specification name
          values: spec.values
            .filter((val) => val.value && val.value.trim())
            .map((val) => ({
              id: isEditing ? val.id || 0 : 0,
              value: val.value.trim(), // Trim specification value
            })),
        }))
        .filter((spec) => spec.values.length > 0);

      const payload = {
        id: isEditing ? values.id : 0,
        productCode: values.productCode || "",
        productType: values.productType.toUpperCase(),
        name: values.name,
        description: values.description || "",
        status: values.status.toUpperCase(),
        tags: values.tags || "",
        unit: values.unit || "MM",
        categoryId: values.categoryId || null,
        brandId: values.brandId || null,
        clientId: values.clientId || null,
        operationCategoryId: values.operationCategoryId || null,
        rawMaterialIds: values.rawMaterialIds || [],
        specifications: cleanedSpecifications,
      };

      if (isEditing) {
        await axiosInstance.put(`${baseUrl}/products`, payload);
        toast.success("Product updated successfully.");
      } else {
        await axiosInstance.post(`${baseUrl}/products`, payload);
        toast.success("Product created successfully.");
      }

      fetchProducts();
      resetForm();
      setIsEditing(false);
      setShowForm(false);

      // Reset category states
      setSelectedMainCategory(null);
      setSelectedSubCategory(null);
      setMainCategoryDetails(null);
      setSubCategoryDetails(null);

      markPageAsCompleted("Products");
    } catch (error) {
      console.error("Error submitting product form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const tableColumns = [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <ColoredCheckbox
            name="select-all"
            color="green"
            checked={selectAll}
            onChange={handleSelectAll}
            value="all"
          />
          <span>Product Name</span>
        </div>
      ),
      accessor: "name",
    },
    { header: "Type", accessor: "productType" },
    { header: "Description", accessor: "description" },
    { header: "Unit", accessor: "unit" },
    { header: "Category", accessor: "category" },
    { header: "Brand", accessor: "brand" },
    { header: "Tags", accessor: "tags" },
    { header: "Pricing Info", accessor: "pricingInfo" }, // Add this
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const transformedData = (bulkUpdateEnabled ? editableProducts : products).map(
    (product) => ({
      name: (
        <div className="product-name-cell">
          <ColoredCheckbox
            name={`product-${product.id}`}
            color="green"
            checked={selectedProducts.includes(product.id)}
            onChange={() => handleSelectProduct(product.id)}
            value={product.id}
          />
          <span className="product-avatar" aria-hidden="true">
            {(product.name || "?").charAt(0).toUpperCase()}
          </span>
          {bulkUpdateEnabled ? (
            <input
              type="text"
              value={product.name || ""}
              onChange={(e) =>
                handleInlineEdit(product.id, "name", e.target.value)
              }
              className="inline-edit-input"
            />
          ) : (
            <span className="product-name-text">{product.name || "-"}</span>
          )}
        </div>
      ),

      productType: bulkUpdateEnabled ? (
        <select
          value={product.productType || "TRADING"}
          onChange={(e) =>
            handleInlineEdit(product.id, "productType", e.target.value)
          }
          className="inline-edit-input"
        >
          <option value="TRADING">Trading</option>
          <option value="MANUFACTURING">Manufacturing</option>
        </select>
      ) : (
        product.productType || "-"
      ),

      description: bulkUpdateEnabled ? (
        <input
          type="text"
          value={product.description || ""}
          onChange={(e) =>
            handleInlineEdit(product.id, "description", e.target.value)
          }
          className="inline-edit-input"
        />
      ) : (
        product.description || "-"
      ),

      unit: bulkUpdateEnabled ? (
        <select
          value={product.unit || ""}
          onChange={(e) => handleInlineEdit(product.id, "unit", e.target.value)}
          className="inline-edit-input"
        >
          <option value="">Select Unit</option>
          {Object.entries(UNIT_CATEGORIES).map(([category, units]) => (
            <optgroup key={category} label={category}>
              {units.map((unit) => (
                <option key={unit} value={unit}>
                  {unit.replace(/_/g, " ")}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      ) : (
        product.unit || "-"
      ),

      tags: bulkUpdateEnabled ? (
        <input
          type="text"
          value={product.tags || ""}
          onChange={(e) => handleInlineEdit(product.id, "tags", e.target.value)}
          className="inline-edit-input"
          placeholder="Enter tags comma-separated"
        />
      ) : product.tags ? (
        <div className="tag-list">
          {product.tags.split(",").map((tag, i) => (
            <span key={i} className="tag-chip">
              {tag.trim()}
            </span>
          ))}
        </div>
      ) : (
        "-"
      ),

      category: bulkUpdateEnabled ? (
        <select
          value={product.category?.id || product.categoryId || ""}
          onChange={(e) =>
            handleInlineEdit(
              product.id,
              "categoryId",
              parseInt(e.target.value) || null,
            )
          }
          className="inline-edit-input"
        >
          <option value="">Select</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      ) : (
        product.category?.name || "-"
      ),

      brand: bulkUpdateEnabled ? (
        <select
          value={product.brand?.id || product.brandId || ""}
          onChange={(e) =>
            handleInlineEdit(
              product.id,
              "brandId",
              parseInt(e.target.value) || null,
            )
          }
          className="inline-edit-input"
        >
          <option value="">Select</option>
          {brands.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.brandName || brand.name}
            </option>
          ))}
        </select>
      ) : (
        product.brand?.brandName || product.brand?.name || "-"
      ),

      pricingInfo: (() => {
        const pricing = productsPricingData[product.id];
        if (!pricing || pricing.length === 0) {
          return <span style={{ color: "#999" }}>No pricing data</span>;
        }

        return (
          <div style={{ fontSize: "12px", maxWidth: "200px" }}>
            <div style={{ fontWeight: "600", marginBottom: "4px" }}>
              {pricing.length} combination{pricing.length > 1 ? "s" : ""}
            </div>
            {pricing.slice(0, 2).map((combo, idx) => (
              <div
                key={idx}
                style={{
                  padding: "4px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  marginBottom: "2px",
                }}
              >
                {/* ✅ Combination name */}
                <div style={{ fontSize: "11px", fontWeight: 600 }}>
                  {combo.combinationName || "Unnamed Combination"}
                </div>

                {/* SKU */}
                <div style={{ fontSize: "11px", color: "#666" }}>
                  SKU: {combo.skuCode || "-"}
                </div>

                {/* Pricing */}
                <div style={{ fontSize: "11px" }}>
                  ₹{combo.costPrice?.toFixed(2) || "0.00"} | Margin:{" "}
                  {combo.marginPercentage?.toFixed(0) || "0"}% | Stock:{" "}
                  {combo.minimumStockQuantity || "0"}
                </div>
              </div>
            ))}

            {pricing.length > 2 && (
              <div
                style={{ color: "#666", fontSize: "11px", marginTop: "4px" }}
              >
                +{pricing.length - 2} more...
              </div>
            )}
          </div>
        );
      })(),

      status: bulkUpdateEnabled ? (
        <select
          value={product.status || "ENABLED"}
          onChange={(e) =>
            handleInlineEdit(product.id, "status", e.target.value)
          }
          className="inline-edit-input"
        >
          <option value="ENABLED">Enabled</option>
          <option value="DISABLED">Disabled</option>
        </select>
      ) : (
        <div className="product-status-cell">
          <ToggleButton
            isOn={product.status === "ENABLED"}
            onToggle={() => toggleProductStatus(product.id, product.status)}
          />
          <span
            className={`product-status-caption ${
              product.status === "ENABLED" ? "is-enabled" : "is-disabled"
            }`}
          >
            {product.status === "ENABLED"
              ? "Available for use"
              : "Hidden from selection"}
          </span>
        </div>
      ),

      actions: !bulkUpdateEnabled && (
        <div style={{ display: "flex", gap: "8px" }}>
          <IconButton
            icon="iconamoon:edit-light"
            onClick={() => handleEditProduct(product.id)}
            variant="edit"
            title="Edit Pricing"
          />
          <IconButton
            icon="solar:money-bag-linear"
            onClick={() => handleUpdatePricing(product.id)}
            variant="primary"
            title="Update Pricing"
          />
          <IconButton
            icon="material-symbols:add-circle-outline"
            onClick={() => handleAddCustomCombination(product.id)}
            variant="primary"
            title="Add Custom Combination"
          />
          <IconButton
            icon="fluent:delete-28-regular"
            onClick={() => handleDelete(product.id)}
            variant="delete"
            title="Delete Pricing"
          />
        </div>
      ),

      originalProduct: product,
      searchableText: `${product.name} ${product.productCode || ""} ${
        product.productType || ""
      } ${product.status || ""} ${product.description || ""} ${
        product.category?.name || ""
      } ${product.brand?.brandName || product.brand?.name || ""} ${
        product.tags || ""
      } ${product.unit || ""}`,
    }),
  );

  const allowedRoles = [
    "ROLE_CRM-ADMIN",
    "ROLE_INVENTORY-MANAGER",
    "ROLE_ERP-ADMIN",
  ];

  if (!allowedRoles.some((role) => user?.authorities?.includes(role))) {
    return <AccessDenied />;
  }

  return (
    <div className="products">
      {/* Metric summary tiles - derived from real product data */}
      <div className="products__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Products</span>
            <span className="metric-tile__value">{productStats.total}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--neutral">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Active Products</span>
            <span className="metric-tile__value">{productStats.active}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m5 13 4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Disabled Products</span>
            <span className="metric-tile__value">{productStats.disabled}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--warning">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="m9 9 6 6M15 9l-6 6" />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Priced Products</span>
            <span className="metric-tile__value">{productStats.priced}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--info">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M6 3.5a.5.5 0 0 1 .5-.5H11a.5.5 0 0 1 0 1H8.35a3 3 0 0 1 2.53 2H11a.5.5 0 0 1 0 1h-.84a3 3 0 0 1-2.8 2H6.907l4.147 4.146a.5.5 0 0 1-.708.708l-5-5A.5.5 0 0 1 5.5 9h2a2 2 0 0 0 1.937-1.5H6.5a.5.5 0 0 1 0-1h2.937A2 2 0 0 0 6.5 4H6a.5.5 0 0 1-.5-.5" />
            </svg>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="products__tip-banner">
        <span className="products__tip-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.3V16h6v-1.2c0-.9.4-1.7 1-2.3A6 6 0 0 0 12 2Z"
            />
          </svg>
        </span>
        <p>
          <strong>Tip:</strong> Set pricing and specifications before enabling a
          product. Disabling a product hides it from new orders but keeps its
          existing stock and pricing history intact.
        </p>
      </div>

      <div className="products__actions">
        <button
          type="button"
          className={`action-card ${showForm ? "action-card--active" : ""}`}
          onClick={() => {
            if (showForm) {
              setShowForm(false);
              // Reset category states
              setSelectedMainCategory(null);
              setSelectedSubCategory(null);
              setMainCategoryDetails(null);
              setSubCategoryDetails(null);
            } else {
              setInitialValues({
                id: 0,
                productCode: "",
                productType: "TRADING",
                name: "",
                description: "",
                status: "ENABLED",
                tags: "",
                unit: "",
                categoryId: null,
                brandId: null,
                clientId: null,
                operationCategoryId: null,
                rawMaterialIds: [],
                specifications: [
                  {
                    id: 0,
                    specName: "",
                    values: [
                      {
                        id: 0,
                        value: "",
                      },
                    ],
                  },
                ],
              });
              setShowForm(true);
              setIsEditing(false);
              // Reset category states
              setSelectedMainCategory(null);
              setSelectedSubCategory(null);
              setMainCategoryDetails(null);
              setSubCategoryDetails(null);
            }
          }}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={showForm ? "M6 18 18 6M6 6l12 12" : "M12 4v16m8-8H4"}
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {showForm ? "Close Product Form" : "Create Product"}
            </span>
            <span className="action-card-desc">
              {showForm
                ? "Hide the product form"
                : "Add a new product to get started"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card ${bulkUpdateEnabled ? "action-card--success" : ""}`}
          onClick={handleBulkUpdateClick}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {bulkUpdateEnabled ? "Submit Bulk Update" : "Enable Bulk Update"}
            </span>
            <span className="action-card-desc">
              {bulkUpdateEnabled
                ? "Save changes made to multiple products"
                : "Edit multiple products "}
            </span>
          </span>
        </button>

        {bulkUpdateEnabled && (
          <button
            type="button"
            className="action-card action-card--danger-outline"
            onClick={() => {
              setBulkUpdateEnabled(false);
              setEditableProducts([...products]); // revert inline changes
            }}
          >
            <span className="action-card-copy">
              <span className="action-card-title">Cancel Bulk Update</span>
              <span className="action-card-desc">Discard unsaved changes</span>
            </span>
          </button>
        )}

        <button
          type="button"
          className="action-card"
          onClick={() => setShowUploadModal(true)}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 16V4m0 0 4 4m-4-4-4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">Upload Excel</span>
            <span className="action-card-desc">
              Bulk import products from a spreadsheet
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card ${selectedProducts.length !== 1 ? "action-card--disabled" : ""}`}
          onClick={() => {
            if (selectedProducts.length === 1) {
              handleAddCustomCombination(selectedProducts[0]);
            } else {
              toast.warning(
                "Please select exactly one product to add custom combination",
              );
            }
          }}
          disabled={selectedProducts.length !== 1}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">Add Custom Combination</span>
            <span className="action-card-desc">
              {selectedProducts.length === 1
                ? "Add a combination for the selected product"
                : "Select exactly one product to enable"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card ${!pricingEnabled ? "action-card--disabled" : ""}`}
          onClick={handleTopEnablePricing}
          disabled={!pricingEnabled}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M6 3.5a.5.5 0 0 1 .5-.5H11a.5.5 0 0 1 0 1H8.35a3 3 0 0 1 2.53 2H11a.5.5 0 0 1 0 1h-.84a3 3 0 0 1-2.8 2H6.907l4.147 4.146a.5.5 0 0 1-.708.708l-5-5A.5.5 0 0 1 5.5 9h2a2 2 0 0 0 1.937-1.5H6.5a.5.5 0 0 1 0-1h2.937A2 2 0 0 0 6.5 4H6a.5.5 0 0 1-.5-.5" />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">Enable Pricing Update</span>
            <span className="action-card-desc">
              {pricingEnabled
                ? "Edit pricing for the selected products"
                : "Select at least two products to enable"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card action-card--danger-outline ${
            selectedProducts.length === 0 ? "action-card--disabled" : ""
          }`}
          onClick={handleBatchDelete}
          disabled={selectedProducts.length === 0}
        >
          <span className="action-card-copy">
            <span className="action-card-title">
              Bulk Delete ({selectedProducts.length})
            </span>
            <span className="action-card-desc">
              Permanently delete all selected products
            </span>
          </span>
        </button>

        <div className="products__auto-generate-toggle">
          <span className="products__auto-generate-label">
            Auto Generate Combinations
          </span>
          <ToggleButton
            isOn={autoGenerateCombination}
            onToggle={handleToggleAutoGenerate}
            status={loadingAutoGenerate}
          />
        </div>
      </div>

      <div
        className={`products__layout ${
          isSidebarCollapsed ? "products__layout--collapsed" : ""
        }`}
      >
        <div className="products__main">
          {showForm && (
            <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={productValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched }) => {
                  return (
                    <Form>
                      {/* Basic Information - Full Width */}
                      <div className="product-form-left">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                          {isEditing ? "Edit Product" : "Create New Product"}
                        </h3>

                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                          <div className="product-form-group">
                            <label className="label">Product Name *</label>
                            <Field
                              type="text"
                              name="name"
                              className={`form-input ${
                                errors.name && touched.name ? "error" : ""
                              }`}
                              placeholder="Enter product name"
                            />
                            <div className="error-placeholder">
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>

                          <div className="product-form-group">
                            <label className="label">Product Type *</label>
                            <Field
                              as="select"
                              name="productType"
                              className={`form-input ${
                                errors.productType && touched.productType
                                  ? "error"
                                  : ""
                              }`}
                            >
                              <option value="TRADING">Trading</option>
                              <option value="MANUFACTURING">
                                Manufacturing
                              </option>
                            </Field>
                            <div className="error-placeholder">
                              <ErrorMessage
                                name="productType"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>

                          <div className="product-form-group">
                            <label className="label">Unit *</label>
                            <Field name="unit">
                              {({ field, form }) => (
                                <UnitDropdown
                                  name={field.name}
                                  value={field.value}
                                  onChange={(e) =>
                                    form.setFieldValue(
                                      field.name,
                                      e.target.value,
                                    )
                                  }
                                  className={`form-input ${
                                    errors.unit && touched.unit ? "error" : ""
                                  }`}
                                />
                              )}
                            </Field>

                            {isEditing && (
                              <div className="unit-warning">
                                Changing the unit may alter the final calculated
                                quantities.
                              </div>
                            )}

                            <div className="error-placeholder">
                              <ErrorMessage
                                name="unit"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>

                          <div className="product-form-group">
                            <label className="label">Tags</label>
                            <Field
                              type="text"
                              name="tags"
                              className="form-input"
                              placeholder="Comma separated tags"
                            />
                            <div className="error-placeholder">
                              <ErrorMessage
                                name="tags"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>

                          <div className="product-form-group">
                            <label className="label">Brand</label>
                            <Field
                              as="select"
                              name="brandId"
                              className="form-input"
                            >
                              <option value="">Select Brand</option>
                              {brands.map((brand) => (
                                <option key={brand.id} value={brand.id}>
                                  {brand.brandName}
                                </option>
                              ))}
                            </Field>
                            <div className="error-placeholder">
                              <ErrorMessage
                                name="brandId"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>

                          <div className="product-form-group md:col-span-5">
                            <label className="label">Description</label>
                            <Field
                              as="textarea"
                              name="description"
                              className="form-input"
                              placeholder="Product description"
                              rows="3"
                            />
                            <div className="error-placeholder">
                              <ErrorMessage
                                name="description"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* LEFT: Category Selection - 50% */}
                        <div className="product-form-group">
                          <label className="label">Category Hierarchy</label>

                          <Field name="categoryId">
                            {({ field, form }) => (
                              <div className="category-hierarchy-container">
                                {/* Main Categories */}
                                <div className="category-level">
                                  <div className="category-level-header">
                                    <h5>Main Categories</h5>
                                  </div>
                                  <div className="category-buttons-grid">
                                    {categories
                                      .filter((cat) => cat.firstLayer)
                                      .map((category) => (
                                        <button
                                          key={category.id}
                                          type="button"
                                          onClick={() =>
                                            handleMainCategorySelect(
                                              category.id,
                                              form.setFieldValue,
                                            )
                                          }
                                          className={`category-button ${
                                            selectedMainCategory === category.id
                                              ? "selected"
                                              : ""
                                          }`}
                                        >
                                          {category.name}
                                        </button>
                                      ))}
                                  </div>
                                </div>

                                {/* Sub Categories */}
                                {mainCategoryDetails?.subCategories &&
                                  mainCategoryDetails.subCategories.length >
                                    0 && (
                                    <div className="category-level">
                                      <div className="category-level-header">
                                        <h5>Sub Categories</h5>
                                        <span className="category-breadcrumb">
                                          {mainCategoryDetails.name}
                                        </span>
                                      </div>
                                      <div className="category-buttons-grid">
                                        {mainCategoryDetails.subCategories.map(
                                          (subCat) => {
                                            const subCatDetails =
                                              categories.find(
                                                (c) =>
                                                  c.id === subCat.id ||
                                                  c.name === subCat,
                                              );

                                            return (
                                              <button
                                                key={subCat.id || subCat}
                                                type="button"
                                                onClick={() =>
                                                  handleSubCategorySelect(
                                                    subCat.id ||
                                                      subCatDetails?.id,
                                                    form.setFieldValue,
                                                  )
                                                }
                                                className={`category-button ${
                                                  selectedSubCategory ===
                                                  (subCat.id ||
                                                    subCatDetails?.id)
                                                    ? "selected"
                                                    : ""
                                                }`}
                                              >
                                                {subCat.name || subCat}
                                              </button>
                                            );
                                          },
                                        )}
                                      </div>
                                    </div>
                                  )}

                                {/* Further Sub Categories */}
                                {subCategoryDetails?.subCategories &&
                                  subCategoryDetails.subCategories.length >
                                    0 && (
                                    <div className="category-level">
                                      <div className="category-level-header">
                                        <h5>More Categories</h5>
                                        <span className="category-breadcrumb">
                                          {mainCategoryDetails?.name} →{" "}
                                          {subCategoryDetails.name}
                                        </span>
                                      </div>
                                      <div className="category-buttons-grid">
                                        {subCategoryDetails.subCategories.map(
                                          (subCat) => (
                                            <button
                                              key={subCat.id || subCat}
                                              type="button"
                                              onClick={() => {
                                                const categoryId = subCat.id;
                                                if (
                                                  field.value === categoryId
                                                ) {
                                                  // Deselect — fall back to sub or main category
                                                  form.setFieldValue(
                                                    "categoryId",
                                                    selectedSubCategory ||
                                                      selectedMainCategory ||
                                                      null,
                                                  );
                                                  toast.info(
                                                    "Category deselected",
                                                  );
                                                } else {
                                                  form.setFieldValue(
                                                    "categoryId",
                                                    categoryId,
                                                  );
                                                  toast.success(
                                                    "Category selected",
                                                  );
                                                }
                                              }}
                                              className={`category-button ${
                                                field.value === subCat.id
                                                  ? "selected"
                                                  : ""
                                              }`}
                                            >
                                              {subCat.name || subCat}
                                            </button>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}

                                {loadingCategoryDetails && <LoadingScreen />}
                              </div>
                            )}
                          </Field>

                          <div className="error-placeholder">
                            <ErrorMessage
                              name="categoryId"
                              component="div"
                              className="error-message"
                            />
                          </div>
                        </div>

                        {/* RIGHT: Specifications Section - 50% */}
                        <div className="specifications-section">
                          <h4>Specifications</h4>

                          <FieldArray name="specifications">
                            {({ push, remove }) => (
                              <div className="spec-list">
                                {values.specifications.map(
                                  (spec, specIndex) => (
                                    <div key={specIndex} className="spec-card">
                                      <div className="spec-top-row">
                                        <Field
                                          name={`specifications[${specIndex}].specName`}
                                          className="spec-input"
                                          placeholder="Specification name"
                                        />

                                        <button
                                          type="button"
                                          className="spec-remove-btn"
                                          onClick={() => remove(specIndex)}
                                        >
                                          ✕
                                        </button>
                                      </div>

                                      <FieldArray
                                        name={`specifications[${specIndex}].values`}
                                      >
                                        {({
                                          push: pushValue,
                                          remove: removeValue,
                                        }) => (
                                          <div className="spec-values">
                                            {spec.values.map(
                                              (val, valIndex) => (
                                                <div
                                                  key={valIndex}
                                                  className="spec-value-chip"
                                                >
                                                  <Field
                                                    name={`specifications[${specIndex}].values[${valIndex}].value`}
                                                    className="spec-value-input"
                                                    placeholder="Value"
                                                    onKeyDown={(e) => {
                                                      if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        pushValue({
                                                          id: 0,
                                                          value: "",
                                                        });
                                                        setTimeout(() => {
                                                          const inputs =
                                                            document.querySelectorAll(
                                                              `input[name^="specifications[${specIndex}].values["]`,
                                                            );
                                                          if (
                                                            inputs.length > 0
                                                          ) {
                                                            inputs[
                                                              inputs.length - 1
                                                            ].focus();
                                                          }
                                                        }, 50);
                                                      }
                                                    }}
                                                  />
                                                  <button
                                                    type="button"
                                                    className="chip-remove"
                                                    onClick={() =>
                                                      removeValue(valIndex)
                                                    }
                                                  >
                                                    ✕
                                                  </button>
                                                </div>
                                              ),
                                            )}

                                            {/* FIX: Add type="button" and prevent event bubbling */}
                                            <IconButton
                                              type="button"
                                              variant="add"
                                              onClick={(e) => {
                                                e.preventDefault(); // Prevent form submission
                                                e.stopPropagation(); // Stop event bubbling
                                                pushValue({ id: 0, value: "" });
                                                setTimeout(() => {
                                                  const inputs =
                                                    document.querySelectorAll(
                                                      `input[name^="specifications[${specIndex}].values["]`,
                                                    );
                                                  if (inputs.length > 0) {
                                                    inputs[
                                                      inputs.length - 1
                                                    ].focus();
                                                  }
                                                }, 50);
                                              }}
                                              icon="material-symbols:add"
                                            />
                                          </div>
                                        )}
                                      </FieldArray>
                                    </div>
                                  ),
                                )}

                                <ButtonWithIcon
                                  type="button"
                                  className="spec-add-btn"
                                  text={"+ Add Specification"}
                                  onClick={() =>
                                    push({
                                      id: 0,
                                      specName: "",
                                      values: [{ id: 0, value: "" }],
                                    })
                                  }
                                />
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      </div>

                      {/* Raw Materials Selection - Only for MANUFACTURING */}
                      {values.productType === "MANUFACTURING" && (
                        <div className="product-form-group mb-6">
                          <label className="label">
                            Raw Materials (Optional)
                          </label>{" "}
                          {/* Search Input */}
                          <div className="raw-material-search-wrapper mb-4">
                            <input
                              type="text"
                              placeholder="Search raw materials..."
                              value={rawMaterialSearch}
                              onChange={(e) =>
                                setRawMaterialSearch(e.target.value)
                              }
                              className="form-input raw-material-search"
                            />
                          </div>
                          <Field name="rawMaterialIds">
                            {({ field, form }) => {
                              const filteredMaterials = rawMaterials.filter(
                                (material) => {
                                  const searchLower =
                                    rawMaterialSearch.toLowerCase();
                                  return (
                                    material.name
                                      ?.toLowerCase()
                                      .includes(searchLower) ||
                                    material.description
                                      ?.toLowerCase()
                                      .includes(searchLower) ||
                                    material.brand?.name
                                      ?.toLowerCase()
                                      .includes(searchLower) ||
                                    material.materialCode
                                      ?.toLowerCase()
                                      .includes(searchLower)
                                  );
                                },
                              );

                              return (
                                <div
                                  className="raw-materials-selection-compact"
                                  style={{ height: "250px" }}
                                >
                                  <div className="raw-materials-compact-grid">
                                    {filteredMaterials.length > 0 ? (
                                      filteredMaterials.map((material) => {
                                        const isSelected =
                                          field.value?.includes(material.id) ||
                                          false;
                                        return (
                                          <div
                                            key={material.id}
                                            className={`raw-material-compact-card ${
                                              isSelected ? "selected" : ""
                                            }`}
                                            onClick={() => {
                                              const currentIds =
                                                field.value || [];
                                              const newIds =
                                                currentIds.includes(material.id)
                                                  ? currentIds.filter(
                                                      (id) =>
                                                        id !== material.id,
                                                    )
                                                  : [
                                                      ...currentIds,
                                                      material.id,
                                                    ];
                                              form.setFieldValue(
                                                "rawMaterialIds",
                                                newIds,
                                              );
                                            }}
                                          >
                                            <div className="compact-card-content">
                                              <ColoredCheckbox
                                                name={`raw-material-${material.id}`}
                                                color="green"
                                                checked={isSelected}
                                                onChange={() => {}}
                                                value={material.id}
                                              />
                                              <div className="compact-card-info">
                                                <span
                                                  className="compact-name"
                                                  title={material.name}
                                                >
                                                  {material.name}
                                                </span>
                                                {material.brand?.name && (
                                                  <span className="compact-brand">
                                                    {material.brand.name}
                                                  </span>
                                                )}
                                                {material.specifications &&
                                                  material.specifications
                                                    .length > 0 && (
                                                    <div className="compact-specs">
                                                      {material.specifications
                                                        .slice(0, 2)
                                                        .map((spec, idx) => (
                                                          <span
                                                            key={idx}
                                                            className="compact-spec-tag"
                                                            title={
                                                              spec.specName
                                                            }
                                                          >
                                                            {spec.specName}
                                                          </span>
                                                        ))}
                                                      {material.specifications
                                                        .length > 2 && (
                                                        <span className="compact-spec-more">
                                                          +
                                                          {material
                                                            .specifications
                                                            .length - 2}
                                                        </span>
                                                      )}
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <div className="no-materials-found">
                                        <p>No raw materials found</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }}
                          </Field>
                          <div className="error-placeholder">
                            <ErrorMessage
                              name="rawMaterialIds"
                              component="div"
                              className="error-message"
                            />
                          </div>
                        </div>
                      )}

                      {/* Submit Button */}
                      <div className="form-actions">
                        <div className="product-form-group button-group">
                          <ButtonWithIcon
                            icon="tdesign:send"
                            text={
                              isEditing ? "Update Product" : "Create Product"
                            }
                            type="submit"
                            className={
                              isEditing ? "edit-button" : "submit-button"
                            }
                          />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          )}

          {showUploadModal && (
            <div className="upload-modal-overlay">
              <div className="upload-modal">
                <div className="modal-header">
                  <h3>Bulk Upload Product Details</h3>
                  <button
                    className="close-button"
                    onClick={() => setShowUploadModal(false)}
                  >
                    ×
                  </button>
                </div>

                <div className="modal-body">
                  <button
                    className="download-template-button"
                    onClick={downloadTemplate}
                  >
                    Download Template
                  </button>

                  <div className="file-input-container">
                    <label
                      htmlFor="product-upload-file"
                      className="file-upload-label"
                    >
                      {selectedFile ? selectedFile.name : "Choose Excel File"}
                    </label>
                    <input
                      id="product-upload-file"
                      type="file"
                      accept=".xlsx,.xls"
                      className="file-input"
                      onChange={handleFileChange}
                    />
                  </div>

                  <button
                    className="upload-button"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                  >
                    Upload
                  </button>

                  <button
                    className="cancel-button"
                    onClick={() => setShowUploadModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Modal */}
          {showPricingModal && selectedProductForPricing && (
            <div className="pricing-modal-overlay">
              <div className="pricing-modal">
                <div className="pricing-modal-header">
                  <div className="pricing-modal-title">
                    <h3>
                      Update Pricing for {selectedProductForPricing.name}
                      {openedFromTopButton &&
                        selectedProductsQueue.length > 1 && (
                          <span
                            style={{
                              marginLeft: "10px",
                              fontSize: "0.9em",
                              color: "#666",
                            }}
                          >
                            ({currentProductIndex + 1} of{" "}
                            {selectedProductsQueue.length})
                          </span>
                        )}
                    </h3>
                    <button
                      className="close-button"
                      onClick={() => {
                        setShowPricingModal(false);
                        setSelectedProductForPricing(null);
                        setPricingUpdateEnabled(false);
                        setSelectedProductsQueue([]);
                        setCurrentProductIndex(0);
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {pricingUpdateEnabled && (
                    <div className="pricing-modal-actions">
                      <div className="pricing-info">
                        <svg
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          className="info-icon"
                        >
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm.93-8.588-.862.49A.502.502 0 0 0 8 7.293v3.002a.5.5 0 0 0 .5.5h.003a.5.5 0 0 0 .5-.5V8.07l1.042-.593A.5.5 0 0 0 10 7V5.5a.5.5 0 0 0-.74-.44zM8 4a1 1 0 1 0 .001 2.001A1 1 0 0 0 8 4z" />
                        </svg>
                        <span>Copy uses the first row pricing</span>
                      </div>

                      <button
                        className="copy-btn"
                        onClick={handleCopyPricingToAll}
                      >
                        Copy Pricing to All
                      </button>

                      <button
                        className="submit-btn"
                        onClick={handleSubmitPricing}
                      >
                        Submit Pricing
                      </button>
                    </div>
                  )}
                </div>

                <div className="pricing-table-container">
                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>Combination</th>
                        <th>Combination Name</th>
                        <th>SKU Code</th>
                        <th>HSN Code</th>
                        <th>Available</th>
                        <th>Cost Price</th>
                        <th>Margin %</th>
                        <th>Discount Price</th>
                        <th>Tax Rate %</th>
                        <th>Min Stock Qty</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pricingUpdateEnabled
                        ? editablePricingCombinations
                        : pricingCombinations
                      ).map((combo, index) => (
                        <tr key={combo.combinationId}>
                          <td>
                            <div className="combination-specs">
                              {combo.specificationValues.map((sv, idx) => (
                                <div key={idx} className="spec-value-tag">
                                  <span className="spec-name">
                                    {sv.specificationName}:
                                  </span>
                                  <span className="spec-value">{sv.value}</span>
                                </div>
                              ))}
                            </div>
                          </td>

                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="text"
                                value={combo.combinationName || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "combinationName",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                placeholder="Optional name"
                              />
                            ) : (
                              <span>{combo.combinationName || "-"}</span>
                            )}
                          </td>

                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="text"
                                value={combo.skuCode || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "skuCode",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                placeholder="SKU Code *"
                                required
                              />
                            ) : (
                              <span>{combo.skuCode || "-"}</span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="text"
                                value={combo.hsnCode || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "hsnCode",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                placeholder="HSN Code (Optional)"
                              />
                            ) : (
                              <span>{combo.hsnCode || "-"}</span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="checkbox"
                                checked={combo.available !== false}
                                onChange={(e) => {
                                  const newValue = e.target.checked;
                                  setEditablePricingCombinations((prev) =>
                                    prev.map((c) =>
                                      c.combinationId === combo.combinationId
                                        ? { ...c, available: newValue }
                                        : c,
                                    ),
                                  );
                                }}
                                className="pricing-checkbox"
                              />
                            ) : (
                              <span
                                className={`availability-badge ${
                                  combo.available !== false
                                    ? "available"
                                    : "unavailable"
                                }`}
                              >
                                {combo.available !== false ? "Yes" : "No"}
                              </span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="number"
                                value={combo.costPrice || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "costPrice",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                min="0"
                                step="0.01"
                                placeholder="0"
                              />
                            ) : (
                              <span>
                                ₹{combo.costPrice?.toFixed(2) || "0.00"}
                              </span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="number"
                                value={combo.marginPercentage || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "marginPercentage",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                min="0"
                                step="0.01"
                                placeholder="0"
                              />
                            ) : (
                              <span>
                                {combo.marginPercentage?.toFixed(2) || "0.00"}%
                              </span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="number"
                                value={combo.discountPrice || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "discountPrice",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                min="0"
                                step="0.01"
                                placeholder="0"
                              />
                            ) : (
                              <span>
                                ₹{combo.discountPrice?.toFixed(2) || "0.00"}
                              </span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="number"
                                value={combo.taxRate || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "taxRate",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                              />
                            ) : (
                              <span>
                                {combo.taxRate?.toFixed(2) || "0.00"}%
                              </span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="number"
                                value={combo.minimumStockQuantity || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "minimumStockQuantity",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                min="0"
                                step="1"
                                placeholder="0"
                              />
                            ) : (
                              <span>{combo.minimumStockQuantity || "0"}</span>
                            )}
                          </td>
                          <td>
                            <IconButton
                              icon="fluent:delete-28-regular"
                              variant="delete"
                              disabled={!combo.combinationId}
                              title={
                                combo.combinationId
                                  ? "Delete Combination"
                                  : "Save pricing before deleting"
                              }
                              onClick={() =>
                                handleDeleteCombination(combo.combinationId)
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="products__table-section">
            {productStats.total === 0 ? (
              <div className="products__empty-state">
                <svg
                  className="products__empty-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
                  />
                </svg>
                <h3>No products found</h3>
                <p>
                  Products are what your customers ultimately order. To get
                  started:
                </p>
                <ol className="products__empty-steps">
                  <li>Create your first product</li>
                  <li>Set its pricing and specifications</li>
                  <li>Enable it so it becomes available</li>
                </ol>
                <button
                  type="button"
                  className="action-card action-card--primary"
                  onClick={() => setShowForm(true)}
                >
                  <span className="action-card-copy">
                    <span className="action-card-title">Create Product</span>
                    <span className="action-card-desc">
                      Add your first product
                    </span>
                  </span>
                </button>
              </div>
            ) : (
              <GeneralTable
                title="Products"
                className="table-container"
                columns={tableColumns}
                data={transformedData}
                selectedCount={selectedProducts.length}
                showPagination={true}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          className="products__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside
          className={`products__sidebar ${
            isSidebarCollapsed ? "products__sidebar--collapsed" : ""
          }`}
          aria-hidden={isSidebarCollapsed}
        >
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>

            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Create Product</strong>
                    <span>
                      Add a product with brand, category, unit and
                      specifications.
                    </span>
                  </li>
                  <li>
                    <strong>Set Pricing</strong>
                    <span>
                      Configure cost, margin and tax for each specification
                      combination.
                    </span>
                  </li>
                  <li>
                    <strong>Enable Product</strong>
                    <span>
                      The product becomes available for orders and inventory.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Set pricing before enabling a product</li>
              <li>Keep product names and tags consistent with your catalog</li>
              <li>
                Use custom combinations for one-off specification variants
              </li>
              <li>
                Review disabled products regularly and clean up unused ones
              </li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Deleting a product is permanent and cannot be undone. Pricing
              combinations and stock records tied to it will also be removed.
            </p>
          </div>
        </aside>
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => popupAction()}
        onDecline={() => setShowWarningPopup(false)}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedProducts.length} selected product${
          selectedProducts.length === 1 ? "" : "s"
        }? This action is permanent and cannot be undone. Pricing and stock data linked to ${
          selectedProducts.length === 1 ? "this product" : "these products"
        } will also be removed.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />

      <CustomCombinationModal
        isOpen={showCustomComboModal}
        onClose={() => {
          setShowCustomComboModal(false);
          setSelectedProductForCombo(null);
        }}
        selectedProduct={selectedProductForCombo}
        onSuccess={handleCustomComboSuccess}
      />
    </div>
  );
};

export default Products;
